import React from 'react'
import { FaFire, FaExclamationTriangle } from 'react-icons/fa'
import { FiRefreshCw, FiHome } from 'react-icons/fi'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    })
    
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    
    // You could also log to an error reporting service here
    // logErrorToService(error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-primary-900/30 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-red-100 to-primary-100 dark:from-red-900/30 dark:to-primary-900/30 flex items-center justify-center">
                <FaExclamationTriangle className="text-4xl text-red-600 dark:text-red-400 animate-pulse" />
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Oops! Something went wrong
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                The application encountered an unexpected error. Don't worry, your code is safe!
              </p>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 mb-8">
              <h2 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-4 flex items-center">
                <FaExclamationTriangle className="mr-2" />
                Error Details
              </h2>
              
              <div className="font-mono text-sm">
                <div className="mb-2">
                  <span className="text-red-700 dark:text-red-400 font-medium">Error:</span>
                  <div className="mt-1 p-3 bg-white dark:bg-gray-900 rounded-lg overflow-x-auto">
                    {this.state.error?.toString() || 'Unknown error'}
                  </div>
                </div>
                
                {this.state.errorInfo && (
                  <div>
                    <span className="text-red-700 dark:text-red-400 font-medium">Component Stack:</span>
                    <div className="mt-1 p-3 bg-white dark:bg-gray-900 rounded-lg overflow-x-auto">
                      <pre className="whitespace-pre-wrap text-xs">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={this.handleReset}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-xl bg-gradient-to-r from-primary-500 to-red-500 text-white font-semibold hover:from-primary-600 hover:to-red-600 transition-all"
              >
                <FiRefreshCw />
                <span>Reload Application</span>
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold transition-all"
              >
                <FiHome />
                <span>Go to Home</span>
              </button>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                If the problem persists, please clear your browser cache or contact support.
              </p>
              <div className="flex items-center justify-center mt-4">
                <FaFire className="text-primary-600 dark:text-primary-400 mr-2 animate-pulse" />
                <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                  CodeFire Error Recovery
                </span>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary